import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Heart, Link2, Music2, RotateCcw, Share2, Sparkles } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const questions = [
  {
    question: "Your ideal first chapter starts with...",
    answers: [["A lingering glance across the room", "slow"], ["A hilarious misunderstanding", "friends"], ["Someone you definitely should not want", "brother"], ["A familiar name returning home", "second"]],
  },
  {
    question: "Your perfect date is...",
    answers: [["A long walk that never wants to end", "slow"], ["Sharing snacks and secrets", "friends"], ["Sneaking away from everyone else", "brother"], ["Recreating an old memory", "second"]],
  },
  {
    question: "The best romance moment is...",
    answers: [["The almost-touch", "slow"], ["Realizing your best friend is home", "friends"], ["The forbidden confession", "brother"], ["The brave second beginning", "second"]],
  },
  {
    question: "Your dream chapter setting is...",
    answers: [["A rainy café with corner seats", "slow"], ["A sunlit kitchen full of inside jokes", "friends"], ["A house party after everyone leaves", "brother"], ["A hometown street at golden hour", "second"]],
  },
  {
    question: "Your happily-ever-after feels like...",
    answers: [["A love worth waiting for", "slow"], ["A best friend becoming forever", "friends"], ["A secret finally spoken aloud", "brother"], ["A heart finding its way back", "second"]],
  },
];

const results = {
  slow: ["Slow Burn Darling", "You adore tension, patience, and the kind of longing that makes every page feel warmer."],
  friends: ["Friends to Lovers", "You believe the best love stories begin with laughter, trust, and someone who already knows your heart."],
  brother: ["Brother's Best Friend", "You love a little danger, a lot of chemistry, and a rule that was made to be beautifully broken."],
  second: ["Second Chance Softie", "You know some stories deserve another beginning, especially when the ending still feels unfinished."],
};

const cardThemes = {
  slow: ["#F8D8E0", "#EBBABF"],
  friends: ["#DCE6DA", "#9CB89B"],
  brother: ["#E3DEEE", "#B7A9CE"],
  second: ["#D8E4ED", "#8EAFC4"],
};

const resultSoundtracks = {
  slow: ["Only Forever — Demi Lovato", "Waste My Time — Zach Gordon", "Something To Lose — James Supercave"],
  friends: ["Lover — G Flip", "How Sweet It Is — James Taylor", "Little Things — One Direction"],
  brother: ["Locksmith — Sadie Jean", "enough for you — Olivia Rodrigo", "Chase Her — Bailey Zimmerman"],
  second: ["The One That Got Away — Boyce Avenue", "Ain't No Sunshine — Bill Withers", "What If I Missed You — Jesse McCartney"],
};

const drawWrappedText = (context, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(" ");
  let line = "";
  let currentY = y;
  words.forEach((word) => {
    const testLine = line ? `${line} ${word}` : word;
    if (context.measureText(testLine).width > maxWidth && line) {
      context.fillText(line, x, currentY);
      line = word;
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  });
  context.fillText(line, x, currentY);
};

export default function RomanceGame() {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({ slow: 0, friends: 0, brother: 0, second: 0 });
  const [result, setResult] = useState(null);
  const question = questions[step];

  const chooseAnswer = (key) => {
    const nextScores = { ...scores, [key]: scores[key] + 1 };
    setScores(nextScores);
    if (step === questions.length - 1) {
      const winner = Object.entries(nextScores).sort((a, b) => b[1] - a[1])[0][0];
      setResult(winner);
    } else {
      setStep(step + 1);
    }
  };

  const resetGame = () => {
    setStep(0);
    setScores({ slow: 0, friends: 0, brother: 0, second: 0 });
    setResult(null);
  };

  const getShareText = () => `I got ${results[result][0]} in Morgan Ella Books' “Which love story are you?” game. Play here: ${window.location.origin}/game`;

  const copyResult = async () => {
    const text = getShareText();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error("Clipboard unavailable");
      }
      toast.success("Your trope result is ready to paste.");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand("copy");
      textarea.remove();
      if (copied) {
        toast.success("Your trope result is ready to paste.");
      } else {
        toast.error("The result could not be copied.");
      }
    }
  };

  const shareResult = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Which love story are you?", text: getShareText(), url: `${window.location.origin}/game` });
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
      }
    }
    await copyResult();
  };

  const downloadResultCard = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1350;
    const context = canvas.getContext("2d");
    const [soft, deep] = cardThemes[result];
    context.fillStyle = "#FCFAF9";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = soft;
    context.beginPath();
    context.arc(120, 90, 360, 0, Math.PI * 2);
    context.arc(1010, 1270, 380, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "rgba(255,255,255,0.72)";
    context.beginPath();
    context.roundRect(100, 130, 880, 1090, 80);
    context.fill();
    context.strokeStyle = "rgba(43,33,36,0.12)";
    context.lineWidth = 3;
    context.stroke();
    context.fillStyle = deep;
    context.beginPath();
    context.moveTo(540, 365);
    context.bezierCurveTo(470, 295, 350, 350, 382, 455);
    context.bezierCurveTo(410, 545, 540, 620, 540, 620);
    context.bezierCurveTo(540, 620, 670, 545, 698, 455);
    context.bezierCurveTo(730, 350, 610, 295, 540, 365);
    context.fill();
    context.textAlign = "center";
    context.fillStyle = "#8A6F77";
    context.font = "30px Outfit";
    context.fillText("M O R G A N   E L L A   B O O K S", 540, 245);
    context.font = "26px Outfit";
    context.fillText("Y O U R   T R O P E", 540, 710);
    context.fillStyle = "#2B2124";
    context.font = "italic 88px 'Cormorant Garamond'";
    drawWrappedText(context, results[result][0], 540, 820, 760, 96);
    context.font = "34px Outfit";
    context.fillStyle = "rgba(43,33,36,0.68)";
    drawWrappedText(context, results[result][1], 540, 980, 760, 50);
    context.font = "26px Outfit";
    context.fillStyle = "#8A6F77";
    context.fillText(`${window.location.origin}/game`, 540, 1140);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("The result card could not be created.");
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${results[result][0].toLowerCase().replaceAll(" ", "-").replaceAll("'", "")}-card.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Your pastel result card is downloading.");
    }, "image/png");
  };

  return (
    <section className="mx-auto max-w-7xl overflow-hidden rounded-[3rem] border border-cocoa/10 bg-lavender/70 p-6 md:p-12" data-testid="romance-game-section">
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
        <div>
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-cocoa/50" data-testid="romance-game-eyebrow"><Sparkles size={16} /> A tiny trope game</p>
          <h2 className="mt-5 font-display text-5xl font-light leading-[0.92] tracking-[-0.04em] md:text-7xl" data-testid="romance-game-title">Which love story are you?</h2>
          <p className="mt-6 max-w-md text-base leading-8 text-cocoa/65" data-testid="romance-game-description">Answer five soft little questions and meet the romance trope currently living in your heart.</p>
        </div>
        <div className="relative rounded-[2.5rem] bg-white/75 p-6 shadow-[0_24px_70px_rgba(43,33,36,0.08)] md:p-8">
          <Heart className="absolute -right-4 -top-4 rotate-12 text-petal" size={70} fill="currentColor" />
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35 }}>
                <p className="text-xs uppercase tracking-[0.25em] text-cocoa/45" data-testid="romance-game-progress">Question {step + 1} of {questions.length}</p>
                <h3 className="mt-5 font-display text-3xl leading-tight md:text-4xl" data-testid="romance-game-question">{question.question}</h3>
                <div className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="romance-game-answers">
                  {question.answers.map(([label, key], answerIndex) => (
                    <button key={label} type="button" onClick={() => chooseAnswer(key)} data-testid={`romance-game-answer-${step + 1}-${answerIndex + 1}`} className="rounded-[1.5rem] border border-cocoa/10 bg-background p-5 text-left text-sm leading-6 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-cocoa/30 hover:bg-petal/40">
                      {label}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div key={result} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} className="text-center" data-testid="romance-game-result">
                <Heart className="mx-auto text-blush" size={44} fill="currentColor" />
                <p className="mt-6 text-xs uppercase tracking-[0.3em] text-cocoa/45">Your result</p>
                <h3 className="mt-4 font-display text-5xl italic leading-none" data-testid="romance-game-result-title">{results[result][0]}</h3>
                <p className="mx-auto mt-5 max-w-md text-sm leading-7 text-cocoa/65" data-testid="romance-game-result-text">{results[result][1]}</p>
                <div className="mx-auto mt-7 max-w-lg rounded-[1.75rem] border border-cocoa/10 bg-background/75 p-5" data-testid="romance-game-soundtrack">
                  <p className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-cocoa/45"><Music2 size={14} /> Trope soundtrack</p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {resultSoundtracks[result].map((song, songIndex) => (
                      <span key={song} data-testid={`romance-game-soundtrack-song-${songIndex + 1}`} className="rounded-full bg-petal/45 px-4 py-2 text-xs text-cocoa/70">{song}</span>
                    ))}
                  </div>
                  <Link to="/playlist" data-testid="romance-game-playlist-link" className="mt-4 inline-block text-xs uppercase tracking-[0.22em] text-cocoa/60 underline decoration-cocoa/25 underline-offset-4 transition-colors duration-300 hover:text-cocoa">
                    Hear the full playlist
                  </Link>
                </div>
                <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="romance-game-result-actions">
                  <button type="button" onClick={shareResult} data-testid="romance-game-share-button" className="inline-flex items-center gap-2 rounded-full bg-cocoa px-6 py-3 text-sm text-white transition-transform duration-300 hover:scale-105">
                    <Share2 size={15} /> Share result
                  </button>
                  <button type="button" onClick={copyResult} data-testid="romance-game-copy-button" className="inline-flex items-center gap-2 rounded-full border border-cocoa/15 px-6 py-3 text-sm transition-colors duration-300 hover:border-cocoa hover:bg-petal/50">
                    <Link2 size={15} /> Copy result
                  </button>
                  <button type="button" onClick={downloadResultCard} data-testid="romance-game-download-button" className="inline-flex items-center gap-2 rounded-full border border-cocoa/15 px-6 py-3 text-sm transition-colors duration-300 hover:border-cocoa hover:bg-petal/50">
                    <Download size={15} /> Download card
                  </button>
                  <button type="button" onClick={resetGame} data-testid="romance-game-reset-button" className="inline-flex items-center gap-2 rounded-full border border-cocoa/15 px-6 py-3 text-sm transition-colors duration-300 hover:border-cocoa hover:bg-petal/50">
                    <RotateCcw size={15} /> Play again
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
